import React from 'react';
import { Box, Typography, Collapse, Alert, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { SnackbarLinkAction } from '@/types/notification';

interface PageLayoutProps {
  title: string;
  children: React.ReactNode;
  leftPanel?: React.ReactNode;
  sidePanel?: React.ReactNode;
  titleActions?: React.ReactNode; // New prop for actions next to title
  leftTitleActions?: React.ReactNode; // New prop for actions left of title (refresh, search)
  selectedItemInfo?: string; // Selected item information to display next to title
  // Content layout props
  contentPadding?: number | string; // Custom padding for content area
  disableContentPadding?: boolean; // Completely disable content padding
  // Notification props
  notificationOpen?: boolean;
  notificationMessage?: string;
  notificationLink?: SnackbarLinkAction;
  notificationType?: 'success' | 'error' | 'warning' | 'info';
  onNotificationClose?: () => void;
}

const PageLayout: React.FC<PageLayoutProps> = ({ 
  title, 
  children, 
  leftPanel,
  sidePanel,
  titleActions,
  leftTitleActions,
  selectedItemInfo,
  contentPadding,
  disableContentPadding = false,
  notificationOpen = false,
  notificationMessage = '',
  notificationLink,
  notificationType = 'success',
  onNotificationClose
}) => {
  return (
    <Box sx={{ 
      display: 'flex', 
      height: '100vh',
      pt: 8, // Offset for fixed header (64px)
      bgcolor: 'background.default'
    }}>
      {/* Left Panel - Takes space in flex layout */}
      {leftPanel && (
        <Box sx={{ 
          flexShrink: 0,
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 64px)' // Account for header
        }}>
          {leftPanel}
        </Box>
      )}

      {/* Main Content Area - Scrollable flex item */}
      <Box sx={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0, // Important: allows flex item to shrink
        height: 'calc(100vh - 64px)', // Account for header
        overflow: 'hidden'
      }}>
        {/* Header section - clean, consistent title bar */}
        <Box sx={(theme) => ({ 
          flexShrink: 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
          height: 64, // Fixed height for consistency
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          gap: 2,
          minWidth: 0,// Allow flex item to shrink
          bgcolor: theme.vars?.palette.grey[50],
          ...((theme.applyStyles?.('dark', {
            bgcolor: theme.vars?.palette.background.paper
          })) ?? {})
        })}>
          {/* Left side - Left actions + Title + Selected Info */}
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            minWidth: 0,
            flex: 1 // Take available space
          }}>
            {leftTitleActions && (
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexShrink: 0 // Don't shrink the action buttons
              }}>
                {leftTitleActions}
              </Box>
            )}
            
            {/* Title - always visible */}
            <Typography 
              variant="h6" 
              component="h1"
              sx={{
                fontSize: '1.1rem',
                fontWeight: 500,
                color: 'text.primary',
                m: 0,
                flexShrink: 0, // Don't shrink the main title
                whiteSpace: 'nowrap'
              }}
            >
              {title}
            </Typography>
            
            {/* Selected Item Info - responsive with ellipsis */}
            {selectedItemInfo && (
              <Typography 
                variant="body2"
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: 'text.primary',
                  fontFamily: 'monospace',
                  px: 2,
                  py: 0.5,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'secondary.main',
                  letterSpacing: '0.025em',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: { xs: '200px', sm: '300px', md: 'none' }, // Responsive max width
                  flexShrink: 1 // Allow to shrink when space is limited
                }}
                title={selectedItemInfo} // Show full text on hover
              >
                {selectedItemInfo}
              </Typography>
            )}
          </Box>
          
          {/* Right side - Title actions */}
          {titleActions && (
            <Box sx={{ 
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexShrink: 0 // Don't shrink the action buttons
            }}>
              {titleActions}
            </Box>
          )}
        </Box>

        {/* Notification area */}
        <Collapse in={notificationOpen}>
          <Box sx={{ px: 3, pt: 2, pb: 0 }}>
            <Alert 
              severity={notificationType} 
              onClose={onNotificationClose}
              sx={{ 
                borderRadius: 1,
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <span>{notificationMessage}</span>
                {notificationLink && (
                  <Link
                    component={RouterLink}
                    to={notificationLink.href}
                    underline="always"
                    color="inherit"
                    sx={{ fontWeight: 600 }}
                    onClick={onNotificationClose}
                  >
                    {notificationLink.label}
                  </Link>
                )}
              </Box>
            </Alert>
          </Box>
        </Collapse>

        {/* Scrollable Content Area */}
        <Box sx={{ 
          flex: 1,
          overflow: 'auto',
          ...(disableContentPadding 
            ? {} 
            : {
                p: contentPadding !== undefined ? contentPadding : 3,
                pt: contentPadding !== undefined ? contentPadding : 2
              }
          )
        }}>
          <Box sx={{
            ...(disableContentPadding 
              ? { p: 0 }
              : {}
            ),
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {children}
            {/* Bottom spacer for scrolling comfort */}
            <Box sx={{ height: 64, flexShrink: 0 }} />
          </Box>
        </Box>
      </Box>

      {/* Right Panel - Takes space in flex layout */}
      {sidePanel && (
        <Box sx={{ 
          flexShrink: 0,
          bgcolor: 'background.paper',
          borderLeft: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 64px)' // Account for header
        }}>
          {sidePanel}
        </Box>
      )}
    </Box>
  );
};

export default PageLayout;
